import { NumberInput } from '@mantine/core'
import { fillLasted, fillUsage, fillWhere, fillsFor, formatClock, formatLitres } from '@racedash/shared'
import { Tile } from '../Tile'
import { useFuelBurn } from '../useFuelBurn'
import type { WidgetEditorProps, WidgetProps } from './props'

/**
 * The last few fills for this car.
 *
 * Each row is when, how much, and where — the stop it went in at, or after the
 * session — with the laps the previous fuel lasted underneath, which is what
 * the litres measure. The newest fill also says how far its own fuel has gone,
 * because that is the figure still moving.
 *
 * Only as good as the matching: a fill the fuel job could not place against a
 * stop says so rather than being given a lap it might not belong to.
 *
 * Above them, the car's burn across the event, where it can be worked out:
 * the figure the fuel strategy is built on.
 */
export default function Fuel({ config, car, data, context }: WidgetProps<'fuel'>) {
  const fills = fillsFor(data.fuel, car).slice(0, config.rows)
  const { burn } = useFuelBurn(context.fuel, car)
  const rates = burn?.rates

  if (!car) {
    return (
      <Tile label="Fuel">
        <span className="dash-dead-text">No car</span>
      </Tile>
    )
  }
  if (fills.length === 0) {
    return (
      <Tile label="Fuel">
        <span className="dash-dead-text">No fills yet</span>
      </Tile>
    )
  }

  return (
    <Tile label="Fuel">
      <div className="dash-list">
        {rates?.green != null && (
          <div className="fuel-burn">
            {rates.green.toFixed(2)} L/lap green
            {rates.fcy != null && ` · ${rates.fcy.toFixed(2)} under FCY`}
          </div>
        )}
        {fills.map((f, i) => {
          const usage = fillUsage(f)
          const lasted = i === 0 ? fillLasted(f) : null
          return (
            <div className="fuel-row" key={f.at.toMillis()}>
              <div className="dash-list-row">
                <span className="dash-list-when">{formatClock(f.at.toDate())}</span>
                <span className="fuel-litres">{formatLitres(f.litres)}</span>
                <span className="fuel-where">{fillWhere(f)}</span>
              </div>
              {(usage || lasted) && (
                <div className="fuel-detail">
                  {usage}
                  {usage && lasted && ' · '}
                  {lasted && <span className="fuel-lasted">on this fuel: {lasted}</span>}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </Tile>
  )
}

export function Editor({ config, onChange }: WidgetEditorProps<'fuel'>) {
  return (
    <NumberInput
      label="Fills to show"
      description="Newest first. The full list is on the Fuel screen."
      min={1}
      max={12}
      value={config.rows}
      onChange={(v) => onChange({ kind: 'fuel', rows: typeof v === 'number' ? v : config.rows })}
    />
  )
}
